/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 📄 File: store/theme.ts
 🧩 Description:
    Theme management store (Zustand + persist).
    DOM application → applyTheme.ts
    Color utilities → themeColorUtils.ts
    Variant maps    → themeVariantColors.ts
────────────────────────────────────────────────────────────────*/

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '../lib/api';
import { applyTheme } from './applyTheme';
import { getInputBackgroundColor } from './themeColorUtils';

// ── Public types ─────────────────────────────────────────────
export type ThemeMode = 'light' | 'dark' | 'system';
export type DarkModeVariant = 'mint' | 'navy' | 'mirage' | 'cinder' | 'black' | 'pure-black' | 'dark-gray' | 'very-dark-gray';
export type LightModeVariant = 'slate' | 'gray' | 'neutral' | 'white' | 'very-light-green' | 'very-light-purple' | 'light-green';

export interface WidgetPrefs {
    borderRadius: number;
    opacity: number;
    headerVisible: boolean;
    shadowIntensity: 'none' | 'light' | 'medium' | 'heavy';
    headerColor: string;
    bodyColor: string;
    textColor: string;
    borderColor: string;
    cardColor: string;
}

export interface ThemePrefs {
    mode?: ThemeMode;
    darkModeVariant?: DarkModeVariant;
    lightModeVariant?: LightModeVariant;
    primaryColor?: string;
    secondaryColor?: string;
    borderRadius?: number;
    tooltips?: {
        lightTheme?: { backgroundColor?: string; textColor?: string; borderRadius?: number; titleBackgroundColor?: string; titleTextColor?: string; };
        darkTheme?: { backgroundColor?: string; textColor?: string; borderRadius?: number; titleBackgroundColor?: string; titleTextColor?: string; };
    };
    notifications?: {
        lightTheme?: { successColor?: string; errorColor?: string; warningColor?: string; infoColor?: string; };
        darkTheme?: { successColor?: string; errorColor?: string; warningColor?: string; infoColor?: string; };
    };
    widget?: WidgetPrefs;
    widgetDark?: WidgetPrefs;
    widgetLight?: WidgetPrefs;
    gridRowHeight?: number;
    gridColumns?: number;
}

// Re-export utility so existing consumers don't break
export { getInputBackgroundColor };

// ── Defaults ─────────────────────────────────────────────────
/** Legacy 'widget' field — kept for backward compat */
const DEFAULT_WIDGET: WidgetPrefs = {
    borderRadius: 12, opacity: 100, headerVisible: true, shadowIntensity: 'medium',
    headerColor: '#1e293b', bodyColor: '#0f172a', textColor: '#f1f5f9', borderColor: '#334155', cardColor: '#1e293b',
};

/** widgetDark — navy-blue palette matching 03_admin_user.sql seed */
const DEFAULT_WIDGET_DARK: WidgetPrefs = {
    borderRadius: 12, opacity: 100, headerVisible: true, shadowIntensity: 'medium',
    headerColor: '#27364f', bodyColor: '#1d283a', textColor: '#f1f5f9', borderColor: '#334155', cardColor: '#243042',
};

const DEFAULT_WIDGET_LIGHT: WidgetPrefs = {
    borderRadius: 12, opacity: 100, headerVisible: true, shadowIntensity: 'medium',
    headerColor: '#ffffff', bodyColor: '#ffffff', textColor: '#1e293b', borderColor: '#e2e8f0', cardColor: '#ffffff',
};

const DEFAULT_THEME_PREFS: ThemePrefs = {
    mode: 'dark',
    darkModeVariant: 'navy',
    lightModeVariant: 'white',
    primaryColor: '#3B82F6',
    secondaryColor: '#64748B',
    borderRadius: 0.5,
    tooltips: {
        lightTheme: { backgroundColor: '#E2E8F0', textColor: '#1E293B', borderRadius: 0.5, titleBackgroundColor: '#F8FAFC', titleTextColor: '#0F172A' },
        darkTheme:  { backgroundColor: '#1A1A1A', textColor: '#F1F5F9', borderRadius: 0.5, titleBackgroundColor: '#1E293B', titleTextColor: '#F8FAFC' },
    },
    notifications: {
        lightTheme: { successColor: '#22c55e', errorColor: '#ef4444', warningColor: '#f59e0b', infoColor: '#3b82f6' },
        darkTheme:  { successColor: '#22c55e', errorColor: '#ef4444', warningColor: '#f59e0b', infoColor: '#3b82f6' },
    },
    widget: DEFAULT_WIDGET,
    widgetDark: DEFAULT_WIDGET_DARK,
    widgetLight: DEFAULT_WIDGET_LIGHT,
    gridRowHeight: 10,
    gridColumns: 48,
};

// ── Store types ───────────────────────────────────────────────
interface ThemeState {
    themeMode: ThemeMode;
    themePrefs: ThemePrefs;
    tenantTheme: { primaryColor: string; secondaryColor: string; logo?: string; };
    isLoading: boolean;
    error: string | null;
}

interface ThemeActions {
    setThemeMode: (mode: ThemeMode) => void;
    updateThemePrefs: (prefs: Partial<ThemePrefs>) => Promise<void>;
    updateThemePrefsLocal: (prefs: Partial<ThemePrefs>) => void;
    loadThemePrefs: () => Promise<void>;
    resetThemePrefs: () => Promise<void>;
    applyTheme: () => void;
    initializeTheme: () => void;
    clearError: () => void;
}

type ThemeStore = ThemeState & ThemeActions;

// ── Store ─────────────────────────────────────────────────────
export const useThemeStore = create<ThemeStore>()(
    persist(
        (set, get) => ({
            // State
            themeMode: 'system',
            themePrefs: DEFAULT_THEME_PREFS,
            tenantTheme: { primaryColor: '#3B82F6', secondaryColor: '#64748B' },
            isLoading: false,
            error: null,

            // ── Actions ──────────────────────────────────────
            setThemeMode: (mode) => {
                get().updateThemePrefs({ mode });
            },

            updateThemePrefs: async (prefs) => {
                const newPrefs = { ...get().themePrefs, ...prefs };
                set({ themePrefs: newPrefs, isLoading: false, error: null });
                get().applyTheme();
                try {
                    await api.put('/api/appearance', newPrefs);
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                } catch (error: any) {
                    console.error('Failed to save theme preferences to database:', error);
                }
            },

            updateThemePrefsLocal: (prefs) => {
                set({ themePrefs: { ...get().themePrefs, ...prefs } });
                get().applyTheme();
            },

            loadThemePrefs: async () => {
                set({ isLoading: true, error: null });
                try {
                    const response = await api.get('/api/appearance');
                    const { themePrefs, tenant } = response.data;
                    const localPrefs = get().themePrefs;

                    const completePrefs: ThemePrefs = {
                        mode: themePrefs?.mode || localPrefs.mode || 'system',
                        darkModeVariant: themePrefs?.darkModeVariant || 'very-dark-gray',
                        lightModeVariant: themePrefs?.lightModeVariant || 'white',
                        primaryColor: themePrefs?.primaryColor || '#3B82F6',
                        secondaryColor: themePrefs?.secondaryColor || '#64748B',
                        tooltips: themePrefs?.tooltips || {
                            lightTheme: { backgroundColor: '#E2E8F0', textColor: '#1E293B', borderRadius: 0.5, titleBackgroundColor: '#F8FAFC', titleTextColor: '#0F172A' },
                            darkTheme:  { backgroundColor: '#1A1A1A', textColor: '#F1F5F9', borderRadius: 0.5, titleBackgroundColor: '#1E293B', titleTextColor: '#F8FAFC' },
                        },
                        notifications: themePrefs?.notifications || {
                            lightTheme: { successColor: '#22c55e', errorColor: '#ef4444', warningColor: '#f59e0b', infoColor: '#3b82f6' },
                            darkTheme:  { successColor: '#22c55e', errorColor: '#ef4444', warningColor: '#f59e0b', infoColor: '#3b82f6' },
                        },
                        animations: themePrefs?.animations || {},
                        // Widget prefs: always trust server values (API now always returns defaults when not set)
                        widget: (themePrefs?.widget && typeof themePrefs.widget === 'object')
                            ? themePrefs.widget as WidgetPrefs
                            : DEFAULT_WIDGET_DARK,
                        widgetDark: (themePrefs?.widgetDark && typeof themePrefs.widgetDark === 'object')
                            ? themePrefs.widgetDark as WidgetPrefs
                            : DEFAULT_WIDGET_DARK,
                        widgetLight: (themePrefs?.widgetLight && typeof themePrefs.widgetLight === 'object')
                            ? themePrefs.widgetLight as WidgetPrefs
                            : DEFAULT_WIDGET_LIGHT,
                        gridRowHeight: typeof themePrefs?.gridRowHeight === 'number' ? themePrefs.gridRowHeight : 10,
                        gridColumns: typeof themePrefs?.gridColumns === 'number' ? themePrefs.gridColumns : 48,
                    } as ThemePrefs;

                    set({
                        themeMode: completePrefs.mode as ThemeMode,
                        themePrefs: completePrefs,
                        tenantTheme: tenant || { primaryColor: '#3B82F6', secondaryColor: '#64748B' },
                        isLoading: false,
                        error: null,
                    });
                    get().applyTheme();
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                } catch (error: any) {
                    console.error('Failed to load theme preferences:', error);
                    set({ isLoading: false, error: error.message || 'Failed to load theme preferences' });
                    throw error;
                }
            },

            resetThemePrefs: async () => {
                const defaultPrefs: ThemePrefs = {
                    mode: 'system',
                    darkModeVariant: 'very-dark-gray',
                    lightModeVariant: 'white',
                    primaryColor: '#3B82F6',
                    secondaryColor: '#64748B',
                    borderRadius: 0.5,
                    tooltips: {
                        lightTheme: { backgroundColor: getInputBackgroundColor('light', 'white'), textColor: '#1E293B', borderRadius: 0.5, titleBackgroundColor: '#F8FAFC', titleTextColor: '#0F172A' },
                        darkTheme:  { backgroundColor: getInputBackgroundColor('dark', undefined, 'very-dark-gray'), textColor: '#F1F5F9', borderRadius: 0.5, titleBackgroundColor: '#1E293B', titleTextColor: '#F8FAFC' },
                    },
                    notifications: {
                        lightTheme: { successColor: '#22c55e', errorColor: '#ef4444', warningColor: '#f59e0b', infoColor: '#3b82f6' },
                        darkTheme:  { successColor: '#22c55e', errorColor: '#ef4444', warningColor: '#f59e0b', infoColor: '#3b82f6' },
                    },
                };
                set({ themeMode: 'system', themePrefs: defaultPrefs, isLoading: false, error: null });
                get().applyTheme();
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                api.put('/api/appearance', defaultPrefs).catch((error: any) => {
                    console.warn('Failed to save reset preferences to server:', error);
                });
            },

            applyTheme: () => {
                const { themeMode, themePrefs, tenantTheme } = get();
                applyTheme(themeMode, themePrefs, tenantTheme);
            },

            initializeTheme: async () => {
                // Apply locally cached theme immediately to prevent flicker
                get().applyTheme();
                
                // Always sync with the server
                try {
                    await get().loadThemePrefs();
                } catch {
                    get().applyTheme();
                }
            },

            clearError: () => set({ error: null }),
        }),
        {
            name: 'theme-storage',
            partialize: (state) => ({ themeMode: state.themeMode, themePrefs: state.themePrefs }),
        }
    )
);
